import _ from '@lodash';
import { MdOutlineInput } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';
import { showMessage } from 'app/store/fuse/messageSlice';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab } from '@material-ui/core';
import EditFormGridComponent from 'modules/ui/editform/subForm/EditFormGridComponent';
import EditFormTextField from 'modules/ui/editform/fields/EditFormTextField';
// import PiMeasurementReaderService from 'app/admin/piMeasurementReader/service/PiMeasurementReaderService';
// import LabMeasurementReaderService from 'app/admin/labMeasurementReader/service/LabMeasurementReaderService';
// import MeasurementService from 'app/admin/measurement/service/MeasurementService';
import EditFormComponentMassSomethingField from '../../common/component/EditFormComponentMassSomethingField';
import MeasurementReaderComponent from '../../common/component/MeasurementReaderComponent';
import PiMeasurementReaderComponent from '../../common/component/PiMeasurementReaderComponent';
import LabMeasurementReaderComponent from '../../common/component/LabMeasurementReaderComponent';
import { useWhatIf } from './WhatIfProvider';
import CopyPasteButtonCluster from '../../common/component/CopyPasteButtonCluster';


function WhatIfStateButton(props) {
	const mainTheme = useSelector(selectMainTheme);

	const { t } = useTranslation(['whatIf']);
	const dispatch = useDispatch();

	const { data, setData } = useWhatIf();

	const [tabValue, setTabValue] = useState('input');
	const [showDialog, setShowDialog] = useState(false);

	// const [piMeasurementReaderItems, setPiMeasurementReaderItems] = useState([]);
	// const [labMeasurementReaderItems, setTabMeasurementReaderItems] = useState([]);
	// const [measurementItems, setMeasurementItems] = useState([]);

	const piMeasurementReaderId = _.get(data, 'properties.piMeasurementReader', null);
	const labMeasurementReaderId = _.get(data, 'properties.labMeasurementReader', null);
	const measurementId = _.get(data, 'properties.measurement', null);

	const methods = useForm({
		defaultValues: { input_attributes: [], output_attributes: [] },
		mode: 'onChange'
	});

	const handleClick = () => {
		setShowDialog(true);
	};

	useEffect(() => {
		if (showDialog) {
			const inputAttributeList = [];
			const outputAttributeList = [];

			data.experiment.attributes.forEach(attribute => {
				if (attribute.io === 'INPUT') inputAttributeList.push(attribute);
				else if (attribute.io === 'OUTPUT') outputAttributeList.push(attribute);
			});

			methods.reset({
				input_attributes: inputAttributeList,
				output_attributes: outputAttributeList
			});

			// if (!piMeasurementReaderItems.length) PiMeasurementReaderService.getListSelection().then(resolve => { setPiMeasurementReaderItems(resolve) }).catch(e => { console.error(e) });
			// if (!labMeasurementReaderItems.length) LabMeasurementReaderService.getListSelection().then(resolve => { setTabMeasurementReaderItems(resolve) }).catch(e => { console.error(e) });
			// if (!measurementItems.length) MeasurementService.getListSelection().then(resolve => { setMeasurementItems(resolve) }).catch(e => { console.error(e) });
		}
	}, [showDialog]);

	const handleLoad = values => {
		const dto = methods.getValues();
		methods.reset({
			input_attributes: dto.input_attributes.map(d => {
				const key = d.model_field_name != null ? d.model_field_name : d.name;
				if (!_.isUndefined(values[key])) {
					return { ...d, value: values[key] };
				}
				return d;
			}),
			output_attributes: dto.output_attributes.map(d => {
				const key = d.model_field_name != null ? d.model_field_name : d.name;
				if (!_.isUndefined(values[key])) {
					return { ...d, value: values[key] };
				}
				return d;
			})
		});
		setTabValue('input');
	};

	const updateAttributes = () => {
		const dto = methods.getValues();
		setData({
			...data,
			experiment: {
				...data.experiment,
				attributes: data.experiment.attributes.map(attribute => {
					if (attribute.io === 'INPUT') {
						const index = dto.input_attributes.findIndex(d => d.id === attribute.id && d.name === attribute.name);
						if (index > -1) {
							return {
								...attribute,
								value: dto.input_attributes[index].value
							};
						}
					} else if (attribute.io === 'OUTPUT') {
						const index = dto.output_attributes.findIndex(d => d.id === attribute.id && d.name === attribute.name);
						if (index > -1) {
							return {
								...attribute,
								value: dto.output_attributes[index].value
							};
						}
					}
					return attribute;
				})
			}
		});
	};

	const getNameForId = (id, items) => items.find(item => item.id === id)?.name ?? null;

	const flattenObject = (obj, parentKey = '', result = {}) => {
		for (const [key, value] of Object.entries(obj)) {
			const newKey = parentKey ? `${parentKey}.${key}` : key;
			if (value && typeof value === 'object' && !Array.isArray(value)) {
				flattenObject(value, newKey, result);
			} else {
				result[newKey] = value;
			}
		}
		return result;
	};

	const handleCopyJson = () => {
		const dto = methods.getValues();
		const merged = [...dto.input_attributes, ...dto.output_attributes];
		const prettyJson = JSON.stringify(merged, null, 2);
		navigator.clipboard.writeText(prettyJson).then(() => {
			dispatch(showMessage({ message: t('to_clipboard_successful') }));
		});
	};

	const handleCopyXls = () => {
		const dto = methods.getValues();
		const merged = [...dto.input_attributes, ...dto.output_attributes];

		const simplified = merged.map(obj => ({
			...obj,
			value: typeof obj.value === 'object' ? JSON.stringify(obj.value) : obj.value
		}));

		const headers = Array.from(new Set(simplified.flatMap(obj => Object.keys(obj))));
		const rows = [
			headers.join('\t'),
			...simplified.map(obj =>
				headers.map(h => obj[h] !== undefined ? obj[h] : '').join('\t')
			)
		];

		const tsv = rows.join('\n');
		navigator.clipboard.writeText(tsv).then(() => {
			dispatch(showMessage({ message: t('to_clipboard_successful') }));
		});
	};

	const parseTsvToJson = (tsv) => {
		const lines = tsv.trim().split('\n');
		if (lines.length < 2) return [];

		const headers = lines[0].split('\t');
		return lines.slice(1).map(line => {
			const values = line.split('\t');
			const obj = {};
			headers.forEach((h, i) => {
				const raw = values[i];
				if (h === 'value') {
					try {
						obj[h] = JSON.parse(raw);
					} catch {
						obj[h] = raw;
					}
				} else {
					obj[h] = raw;
				}
			});
			return obj;
		});
	};

	const handlePaste = () => {
		navigator.clipboard.readText().then(clipText => {
			try {
				let parsed;
				if (clipText.trim().startsWith('[')) {
					parsed = JSON.parse(clipText);
				} else {
					parsed = parseTsvToJson(clipText);
				}
				const inputs = parsed.filter(item => item.io === 'INPUT');
				const outputs = parsed.filter(item => item.io === 'OUTPUT');
				methods.reset({
					input_attributes: inputs,
					output_attributes: outputs
				});
				dispatch(showMessage({ message: t('from_clipboard_successful') }));
			} catch (e) {
				dispatch(showMessage({ message: t('from_clipboard_failed') }));
			}
		});
	};

	return (
		<>
			<Button className="whitespace-nowrap mx-4" variant="contained" color="secondary" disabled={!(data && data.experiment && data.experiment.attributes)} onClick={() => handleClick()} startIcon={<MdOutlineInput className="hidden sm:flex" />} >
				{t('state')}
			</Button>

			<ThemeProvider theme={mainTheme}>

				<Dialog open={showDialog} fullWidth maxWidth="lg">

					<DialogTitle disableTypography className="border-b">

						<div className="flex flex-row w-full">

							<div className="flex mr-24 items-center">{t('state')}</div>

							<Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} aria-label="sum-table-properties-tabs">
								<Tab label={t('input')} value='input' />
								<Tab label={t('output')} value='output' />
								{measurementId != null && <Tab label='Measurement' value='measurement' />}
								{piMeasurementReaderId != null && <Tab label='PI Measurement Reader' value='piMeasurementReader' />}
								{labMeasurementReaderId != null && <Tab label='Lab Measurement Reader' value='labMeasurementReader' />}
							</Tabs>

						</div>

					</DialogTitle>

					<DialogContent className="p-20">

						<div className={tabValue !== 'input' ? 'hidden' : ''}>
							<FormProvider {...methods}>
								<EditFormGridComponent
									config={{ topic: 'whatIf' }}
									fieldConfig={{
										key: 'input_attributes',
										component: EditFormGridComponent,
										modifyOnly: true,
										defaultValue: {
											id: null,
											name: null,
											type: null,
											value: null
										},
										fields: [
											{
												key: 'name',
												component: EditFormTextField,
												required: true
											},
											{
												key: 'value',
												component: EditFormTextField,
												required: true,
												dependsOn: 'type',
												visibleIf: type => type === 'Variable' || type === 'SpreadSheet' || type === null,
												type: 'number'
											},
											{
												key: 'value',
												component: EditFormComponentMassSomethingField,
												dependsOn: 'type',
												visibleIf: type => type === 'ComponentMassFrac'
											}
										]
									}}
								/>
							</FormProvider>
						</div>

						<div className={tabValue !== 'output' ? 'hidden' : ''}>
							<FormProvider {...methods}>
								<EditFormGridComponent
									config={{ topic: 'whatIf' }}
									fieldConfig={{
										key: 'output_attributes',
										component: EditFormGridComponent,
										modifyOnly: true,
										defaultValue: {
											id: null,
											name: null,
											type: null,
											value: null
										},
										fields: [
											{
												key: 'name',
												component: EditFormTextField,
												required: true
											},
											{
												key: 'value',
												component: EditFormTextField,
												required: true,
												dependsOn: 'type',
												visibleIf: type => type === 'Variable' || type === 'SpreadSheet' || type === null,
												type: 'number'
											},
											{
												key: 'value',
												component: EditFormComponentMassSomethingField,
												dependsOn: 'type',
												visibleIf: type => type === 'ComponentMassFrac'
											}
										]
									}}
								/>
							</FormProvider>
						</div>

						{measurementId != null && (<div className={tabValue !== 'measurement' ? 'hidden' : ''}>
							<MeasurementReaderComponent onLoad={values => { console.log('value to load', values); handleLoad(values) }} measurementReaderName={/*getNameForId(measurementId, measurementItems)*/measurementId} />
						</div>)}

						{piMeasurementReaderId != null && (<div className={tabValue !== 'piMeasurementReader' ? 'hidden' : ''}>
							<PiMeasurementReaderComponent onLoad={values => handleLoad(values)} piMeasurementReaderName={/*getNameForId(piMeasurementReaderId, piMeasurementReaderItems)*/piMeasurementReaderId} />
						</div>)}

						{labMeasurementReaderId != null && (<div className={tabValue !== 'labMeasurementReader' ? 'hidden' : ''}>
							<LabMeasurementReaderComponent onLoad={values => handleLoad(values)} labMeasurementReaderName={/*getNameForId(labMeasurementReaderId, labMeasurementReaderItems)*/labMeasurementReaderId} />
						</div>)}

					</DialogContent>

					<DialogActions className="justify-center border-t h-76 relative">

						<Button className="absolute left-20" onClick={() => { updateAttributes(); setShowDialog(false); }}>
							{t('common:close')}
						</Button>

						{(tabValue === 'input' || tabValue === 'output') && <CopyPasteButtonCluster onCopyJson={handleCopyJson} onCopyXls={handleCopyXls} onPaste={handlePaste} />}

					</DialogActions>

				</Dialog>

			</ThemeProvider>
		</>
	);
}

export default WhatIfStateButton;